import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, ExternalLink, HandCoins } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
export const productImageUrl = (path) => (path ? `${API_BASE}${path}` : null);
const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n ?? 0);

export default function Store() {
  const [products, setProducts] = useState(null);

  useEffect(() => {
    api.get("/store/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  return (
    <div className="py-12 sm:py-16 lg:py-20" data-testid="store-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">CETF Store</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="store-title">La boutique de l'association</h1>
          <p className="text-base text-slate-600 mt-3">
            T-shirts, goodies et accessoires aux couleurs de l'association. Chaque achat soutient nos actions pour les enfants de Toury.
          </p>
        </div>

        {products === null ? (
          <p className="text-sm text-slate-400">Chargement…</p>
        ) : products.length === 0 ? (
          <Card className="border-slate-200" data-testid="store-empty">
            <CardContent className="p-10 text-center space-y-3">
              <ShoppingBag className="h-10 w-10 text-slate-300 mx-auto" />
              <p className="text-slate-500 text-sm">La boutique ouvre bientôt — revenez vite !</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="store-grid">
            {products.map((p) => (
              <Card key={p.id} className="border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`product-${p.id}`}>
                {p.image_url && (
                  <img src={productImageUrl(p.image_url)} alt={p.name} className="w-full aspect-[4/3] object-cover" data-testid={`product-image-${p.id}`} />
                )}
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="font-semibold text-slate-800 leading-snug">{p.name}</h2>
                    <span className="font-heading font-bold text-emerald-700 shrink-0" data-testid={`product-price-${p.id}`}>
                      {p.price != null ? fmt(p.price) : "Prix libre"}
                    </span>
                  </div>
                  {p.description && <p className="text-sm text-slate-600 leading-relaxed">{p.description}</p>}
                  {p.payment_mode === "link" && p.external_link ? (
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700" asChild data-testid={`product-buy-${p.id}`}>
                      <a href={p.external_link} target="_blank" rel="noopener noreferrer">
                        Commander / payer en ligne <ExternalLink className="h-4 w-4 ml-2" />
                      </a>
                    </Button>
                  ) : (
                    <Button variant="outline" className="w-full" asChild data-testid={`product-contact-${p.id}`}>
                      <Link to="/contact">
                        <HandCoins className="h-4 w-4 mr-2" />Règlement sur place — nous contacter
                      </Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
